import { useTranslation } from 'react-i18next';

import { Nudge } from '@/components/ui/nudge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { FilterMode } from '@/utils/stores/useOfficeContextStore';

import { filterOptionLabelClass } from './constants';

interface FilterOption {
  disabled?: boolean;
  label: string;
  value: FilterMode;
}

interface FilterModeSelectorProps {
  filterMode: FilterMode;
  isComposeMode: boolean;
  onFilterModeChange: (mode: FilterMode) => void;
  options: {
    canUseAllMails: boolean;
    canUseCurrentEmail: boolean;
    canUseCurrentThread: boolean;
    ragSearchEnabled: boolean;
    showSyncingState: boolean;
  };
}

export function FilterModeSelector({
  filterMode,
  isComposeMode,
  onFilterModeChange,
  options,
}: FilterModeSelectorProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 px-3 py-2">
      <RadioGroup
        className="flex flex-col gap-2"
        onValueChange={(val) => onFilterModeChange(val as FilterMode)}
        value={filterMode}
      >
        {!isComposeMode && (
          <>
            <FilterOptionItem
              disabled={!(options.canUseCurrentEmail || options.canUseCurrentThread)}
              label={t('assistant.tabs.currentEmailThread')}
              value="currentEmailThread"
            />
            <Divider />
          </>
        )}
        {options.ragSearchEnabled && (
          <>
            {options.showSyncingState ? (
              <label className="flex cursor-not-allowed items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <span className="bg-elevated-subtle h-3 w-3 rounded-full" />
                  </span>
                  <span className={`${filterOptionLabelClass} text-neutral-mid`}>
                    {t('assistant.tabs.allMails')}
                  </span>
                </span>
                <span className={`shimmer-text ${filterOptionLabelClass} text-neutral-mid`}>
                  {t('assistant.emailContext.syncing')}
                </span>
              </label>
            ) : (
              <Nudge
                align="start"
                description={t('assistant.emailContext.allMailsNudge.dropdownDescription')}
                id="all-emails-option"
                side="top"
                title={t('assistant.emailContext.allMailsNudge.dropdownTitle')}
              >
                <div>
                  <FilterOptionItem label={t('assistant.tabs.allMails')} value="allMails" />
                </div>
              </Nudge>
            )}
            <Divider />
          </>
        )}
        <FilterOptionItem label={t('assistant.tabs.folder')} value="folder" />
        <Divider />
        <FilterOptionItem label={t('assistant.tabs.custom')} value="custom" />
      </RadioGroup>
    </div>
  );
}

const Divider = () => <div className="border-border-subtle border-b" />;

const FilterOptionItem = ({ disabled = false, label, value }: FilterOption) => (
  <label
    className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
  >
    <RadioGroupItem disabled={disabled} value={value} />
    <span className={filterOptionLabelClass}>{label}</span>
  </label>
);
